<template>
    <div v-if="playbook" class="c-view">
        <InternalHeader>
            <div slot="title" class="c-internal-header__title">
                <a @click="$router.push('/playbooks')">
                    <Icon :custom="'icon icon-arrow-left-1'"></Icon>
                    Playbooks
                </a>
                /
                <a
                    @click="
                        $router.push(
                            '/playbooks/' + playbook.id + '/summary-instances'
                        )
                    "
                >
                    {{ playbook.name }}
                </a>
                / Actions
            </div>
        </InternalHeader>
        <Row class="mt-5" :gutter="16">
            <Col span="6">
                <playbook-subnav :playbook-id="playbookId"></playbook-subnav>
            </Col>
            <Col span="18">
                <playbook-status
                    :value="playbook.status_id"
                    @update="mixinPlaybook_savePlaybook"
                ></playbook-status>
                <div v-if="playbookActions != null">
                    <div v-if="playbookActions.length > 0">
                        <playbook-steps-form
                            class="c-playbooks__steps-form"
                            :saving="savingPlaybook"
                            :saving-ids="savingIds"
                            :playbook="playbook"
                            :playbook-actions="playbookActions"
                            :users="users"
                            :roles="roles"
                            :task-types="taskTypes"
                            :message-templates="messageTemplates"
                            :recipient-tags="recipientTags"
                            @update="handleUpdatePlaybookAction"
                            @remove="handleRemovePlaybookAction"
                        ></playbook-steps-form>
                        <add-playbook-action
                            class="mt-5"
                            @add="handleAddPlaybookAction"
                            :messages-enabled="messagesEnabled"
                        ></add-playbook-action>
                    </div>
                    <empty-state v-else class="text-center mt-8">
                        <empty-state-illustration
                            slot="illustration"
                            class="util-max-w200"
                        ></empty-state-illustration>
                        <p slot="text">
                            You haven't added any actions to your Playbook.
                        </p>
                        <div slot="actions">
                            <add-playbook-action
                                class="mt-5"
                                @add="handleAddPlaybookAction"
                                :messages-enabled="messagesEnabled"
                            ></add-playbook-action>
                        </div>
                    </empty-state>
                </div>
                <loading-component v-else></loading-component>
            </Col>
        </Row>
    </div>
</template>

<script>
import Playbook from "@/js/mixins/playbook.js";
import PlaybookAction from "@/js/lookups/playbook-action";

export default {
    props: {
        playbooksRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/playbooks.js"))();
            },
        },
        messageTemplatesRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/base.js"))(
                    window.akita.urls.api + "/v1/message-templates"
                );
            },
        },
        usersRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/base.js"))(
                    window.akita.urls.api + "/v1/users"
                );
            },
        },
        rolesRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/base.js"))(
                    window.akita.urls.api + "/v1/roles"
                );
            },
        },
        segmentsRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/segments.js"))();
            },
        },
        taskTypesRepository: {
            type: Object,
            default: () => {
                return new (require("@/js/repositories/base.js"))(
                    window.akita.urls.api + "/v1/task-types"
                );
            },
        },
    },
    mixins: [Playbook],
    components: {
        InternalHeader: function () {
            return import("@/js/components/partials/header");
        },
        PlaybookStatus: function () {
            return import("@/js/components/partials/playbook-status");
        },
        PlaybookSubnav: function () {
            return import("@/js/components/partials/playbook-subnav");
        },
        PlaybookStepsForm: function () {
            return import("@/js/components/partials/playbook-steps-form");
        },
        AddPlaybookAction: function () {
            return import("@/js/components/partials/playbook-steps-form/add");
        },
        EmptyState: function () {
            return import("@/js/components/partials/empty-state");
        },
        EmptyStateIllustration: function () {
            return import("@/js/components/partials/illustrations/magic");
        },
    },
    data() {
        return {
            savingIds: [],
            playbookActions: null,
            roles: [],
            users: [],
            messageTemplates: [],
            recipientTags: [],
            taskTypes: [],
        };
    },
    created() {
        this.load();
    },
    computed: {
        messagesEnabled() {
            if (this.messageTemplates.length === 0) {
                return false;
            }
            if (
                this.playbook.entity_type_id === 2 &&
                this.messageTemplates.length === 0
            ) {
                return false;
            }
            return true;
        },
    },
    watch: {
        playbook() {
            if (this.playbook) {
                this.$root.$emit("page-title:update", this.playbook.name);
            }
        },
    },
    methods: {
        async load() {
            await Promise.all([
                this.queryPlaybookActions(),
                this.queryUsers(),
                this.queryRoles(),
                this.queryMessageTemplates(),
                this.queryRecipientTags(),
                this.queryTaskTypes(),
            ]);
        },
        async queryPlaybookActions() {
            let response = await this.playbooksRepository
                .actions(this.playbookId)
                .index({
                    params: {
                        q: [],
                        sort_by: "sort_order:asc",
                    },
                });

            this.playbookActions = response.data.data;
        },
        async queryTaskTypes() {
            let page = 1;
            let finished = false;
            let request;
            let response;
            this.taskTypes = [];
            while (!finished) {
                request = {
                    params: {
                        q: [],
                        sort_by: "name:asc",
                        page: page,
                        per_page: 50,
                        include: [],
                    },
                };
                response = await this.taskTypesRepository.index(request);
                this.taskTypes.push(...response.data.data);
                finished = _.get(response, "data.links.next") ? false : true;
                ++page;
            }
        },
        async queryUsers() {
            let page = 1;
            let finished = false;
            let request;
            let response;
            this.users = [];
            while (!finished) {
                request = {
                    params: {
                        q: ["status_id[eq]=1"],
                        sort_by: "name:asc",
                        page: page,
                        per_page: 50,
                        include: [],
                    },
                };
                response = await this.usersRepository.index(request);
                this.users.push(...response.data.data);
                finished = _.get(response, "data.links.next") ? false : true;
                ++page;
            }
        },
        async queryMessageTemplates() {
            let page = 1;
            let finished = false;
            let request;
            let response;
            this.messageTemplates = [];
            while (!finished) {
                request = {
                    params: {
                        q: [],
                        sort_by: "name:asc",
                        page: page,
                        per_page: 50,
                        include: [],
                    },
                };
                response = await this.messageTemplatesRepository.index(request);
                this.messageTemplates.push(...response.data.data);
                finished = _.get(response, "data.links.next") ? false : true;
                ++page;
            }
        },
        async queryRecipientTags() {
            let page = 1;
            let finished = false;
            let request;
            let response;
            this.recipientTags = [];
            while (!finished) {
                request = {
                    params: {
                        q: ["tag_enabled[eq]=true", "entity_type_id[eq]=2"],
                        sort_by: "name:asc",
                        page: page,
                        per_page: 50,
                        include: [],
                    },
                };
                response = await this.segmentsRepository.index(request);
                this.recipientTags.push(...response.data.data);
                finished = _.get(response, "data.links.next") ? false : true;
                ++page;
            }
        },
        async queryRoles() {
            let page = 1;
            let finished = false;
            let request;
            let response;
            this.roles = [];
            while (!finished) {
                request = {
                    params: {
                        q: ["can_own_accounts[eq]=true"],
                        sort_by: "name:asc",
                        page: page,
                        per_page: 50,
                        include: [],
                    },
                };
                response = await this.rolesRepository.index(request);
                this.roles.push(...response.data.data);
                finished = _.get(response, "data.links.next") ? false : true;
                ++page;
            }
        },
        async handleUpdatePlaybookAction(playbookAction) {
            // Revalidate
            let definition = new PlaybookAction(playbookAction, this.playbook);

            if (!definition.valid()) {
                return;
            }

            let id = playbookAction.id;
            this.savingIds.push(id);
            if (!playbookAction.id) {
                let response = await this.playbooksRepository
                    .actions(this.playbookId)
                    .store(playbookAction)
                    .catch(() => {
                        // no longer saving
                        this.savingIds = _.filter(this.savingIds, (id) => {
                            return id != playbookAction.id;
                        });
                    });
                playbookAction.id = response.data.data.id;
            } else {
                let response = await this.playbooksRepository
                    .actions(this.playbookId)
                    .update(playbookAction.id, playbookAction)
                    .catch(() => {
                        // no longer saving
                        this.savingIds = _.filter(this.savingIds, (id) => {
                            return id != playbookAction.id;
                        });
                    });
            }
            // no longer saving
            this.savingIds = _.filter(this.savingIds, (id) => {
                return id != playbookAction.id;
            });
        },
        async handleRemovePlaybookAction(index) {
            let playbookAction = _.get(this.playbookActions, index);
            if (!playbookAction) {
                return;
            }
            this.playbookActions.splice(index, 1);
            if (playbookAction.id) {
                let response = await this.playbooksRepository
                    .actions(this.playbookId)
                    .remove(playbookAction.id);
            }
        },
        async handleAddPlaybookAction(typeId) {
            var uuid = require("uuid/v1");
            this.playbookActions.push({
                playbook_id: this.playbook.id,
                type_id: typeId,
                sort_order: this.playbookActions.length,
                trigger_id: 0,
                delay_days: 0,
            });
        },
    },
};
</script>
